import {
  Table,
  Paging,
  EllipsisSpan,
  usePaging,
  useUpdate,
  Form,
  Input,
  TreeSelect,
  CustomFilter,
  Modal,
} from '@maxtropy/components';
import { FC, useState, Key, useEffect } from 'react';
import { DefaultOptionType } from 'rc-tree-select/es/TreeSelect';
import { ColumnType } from 'antd/es/table';
import { StaffOuRequest } from '../../../../api/staff';
import { OuStatus, OuStatusDisplay } from '../../../../api/const';
import { getAllOrganizationTree } from '../../../../api/customer';
import { formatTreeData } from '../../../../lib/util';
import { OuOperationType, OuOperationTypeDisplay } from '../util';
import { apiV2StaffOuPageV2Post, V2StaffOuPageV2PostResponse } from '@maxtropy/cc-customer-apis-v2';

export type OuResponse = Exclude<V2StaffOuPageV2PostResponse['list'], undefined>[number];

export const ouColumns: ColumnType<OuResponse>[] = [
  {
    title: '运营单元编号',
    dataIndex: 'serialNumber',
    width: 200,
    render: v => <EllipsisSpan value={v} />,
  },
  {
    title: '运营单元名称',
    dataIndex: 'name',
    width: 200,
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '状态',
    width: 100,
    dataIndex: 'status',
    render: (v: OuStatus) => <EllipsisSpan value={OuStatusDisplay[v]} />,
  },
  {
    title: '所属组织',
    width: 300,
    dataIndex: 'customerName',
    render: (v: number) => <EllipsisSpan value={String(v)} />,
  },
];

interface OuModalProps {
  type: OuOperationType;
  selectedOuData: OuResponse[];
  setSelectedOuData: (value: OuResponse[]) => void;
  onCancel: () => void;
}

type SearchParams = Omit<StaffOuRequest, 'size' | 'page' | 'type'>;

const OuModal: FC<OuModalProps> = ({ type, selectedOuData, setSelectedOuData, onCancel }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [updateState] = useUpdate();

  const [searchForm] = Form.useForm();

  const [treeData, setTreeData] = useState<DefaultOptionType[]>([]);

  const [data, setData] = useState<Array<OuResponse>>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<OuResponse[]>([]);

  const pagingInfo = usePaging(100);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;

  const [searchParams, setSearchParams] = useState<SearchParams>();

  useEffect(() => {
    setLoading(true);
    apiV2StaffOuPageV2Post({
      ...searchParams,
      page: pageOffset,
      size: pageSize,
    })
      .then(res => {
        setData(res.list ?? []);
        setTotalCount(res.total ?? 0);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageOffset, type, pageSize, searchParams, updateState]);

  useEffect(() => {
    getAllOrganizationTree().then(res => {
      setTreeData(formatTreeData([res]));
    });
  }, []);

  useEffect(() => {
    if (selectedOuData.length > 0) {
      setSelectedRowKeys(selectedOuData.map(record => record.id!));
      setSelectedRows(selectedOuData);
    }
  }, [selectedOuData]);

  const onOk = () => {
    setSelectedOuData(selectedRows.map(item => ({ ...item })));
    onCancel();
  };

  const rowSelection = {
    onChange: (selectedRowKeys: Key[], selectedRows: OuResponse[]) => {
      setSelectedRows(selectedRows);
      setSelectedRowKeys(selectedRowKeys);
    },
    preserveSelectedRowKeys: true,
    selectedRowKeys: selectedRowKeys,
  };

  const onFinish = (record: SearchParams) => {
    setPageOffset(1);
    setSearchParams(record);
  };

  const onReset = () => {
    setPageOffset(1);
    setSearchParams({});
  };

  return (
    <Modal
      size="big"
      open={true}
      title={`${OuOperationTypeDisplay[type]}-运营单元选择`}
      onOk={onOk}
      onCancel={onCancel}
      destroyOnClose
    >
      <CustomFilter<SearchParams> form={searchForm} onFinish={onFinish} onReset={onReset} colSpan={8}>
        <Form.Item name="ouName" label="运营单元名称">
          <Input placeholder="请输入名称查询" maxLength={20} />
        </Form.Item>
        <Form.Item name="mcidList" label="所在组织">
          <TreeSelect placeholder="请选择" treeData={treeData} multiple />
        </Form.Item>
      </CustomFilter>
      <Table
        loading={loading}
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        sticky
        scroll={{ y: 300 }}
        rowKey="id"
        columns={ouColumns}
        dataSource={data}
        showSelectedCount
        selectedCount={selectedRows.length}
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </Modal>
  );
};

export default OuModal;
